(() => {
  'use strict';
  if (window.RaversRack) { window.RaversRack.scan(); return; }
  // Original product images stay unchanged. Vector display masks preserve light
  // fabric and exclude the photographed frame; legacy white knockout is optional.
  const instances = new WeakMap();
  const artworkCache = new Map();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const compact = matchMedia('(max-width: 700px)');

  function readArtwork(src, knockout, region = '', clip = '') {
    const key = `${src}|${knockout}|${region}|${clip}`;
    if (artworkCache.has(key)) return artworkCache.get(key);
    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        try {
          const size = 720;
          const isAtlas = region === 'left' || region === 'right';
          const sourceW = isAtlas ? image.naturalWidth / 2 : image.naturalWidth;
          const sourceX = region === 'right' ? sourceW : 0;
          const sourceH = image.naturalHeight;
          const ratio = Math.min(1, size / Math.max(sourceW, sourceH));
          const w = Math.round(sourceW * ratio), h = Math.round(sourceH * ratio);
          const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          const shape = window.RCRackShapes?.[clip];
          if (clip && clip !== 'none' && !shape) throw new Error('Unknown garment display mask');
          if (shape) {
            ctx.save();
            ctx.scale(w / shape.width, h / shape.height);
            ctx.clip(new Path2D(shape.path));
            ctx.setTransform(1, 0, 0, 1, 0, 0);
          }
          ctx.drawImage(image, sourceX, 0, sourceW, sourceH, 0, 0, w, h);
          if (shape) ctx.restore();
          const pixels = ctx.getImageData(0, 0, w, h), rgba = pixels.data;
          if (knockout || shape?.backgroundThreshold) {
            const threshold = shape?.backgroundThreshold || 252;
            const seen = new Uint8Array(w * h), queue = new Uint32Array(w * h);
            let head = 0, tail = 0;
            function enqueue(i) {
              if (seen[i]) return;
              const p = i * 4;
              const cutoff = shape?.backgroundThreshold && i < w * Math.round(h * .18) ? 210 : threshold;
              if (rgba[p + 3] === 0 || (rgba[p] >= cutoff && rgba[p + 1] >= cutoff && rgba[p + 2] >= cutoff)) {
                seen[i] = 1; queue[tail++] = i;
              }
            }
            for (let x = 0; x < w; x++) { enqueue(x); enqueue((h - 1) * w + x); }
            for (let y = 0; y < h; y++) { enqueue(y * w); enqueue(y * w + w - 1); }
            while (head < tail) {
              const i = queue[head++]; rgba[i * 4 + 3] = 0;
              const x = i % w;
              if (x > 0) enqueue(i - 1); if (x < w - 1) enqueue(i + 1);
              if (i >= w) enqueue(i - w); if (i < w * (h - 1)) enqueue(i + w);
            }
            // Feather only boundary pixels; light lettering inside the print survives.
            for (let i = 0; i < w * h; i++) {
              if (seen[i]) continue;
              const x = i % w;
              if ((x > 0 && seen[i - 1]) || (x < w - 1 && seen[i + 1]) || (i >= w && seen[i - w]) || (i < w * (h - 1) && seen[i + w])) {
                const p = i * 4, whiteness = Math.min(rgba[p], rgba[p + 1], rgba[p + 2]);
                const cutoff = shape?.backgroundThreshold && i < w * Math.round(h * .18) ? 210 : threshold;
                const featherStart = shape?.backgroundThreshold ? cutoff - 25 : 220;
                if (whiteness > featherStart) rgba[p + 3] = Math.min(rgba[p + 3], Math.max(0, Math.round(255 * (cutoff - whiteness) / (cutoff - featherStart))));
              }
            }
            if (shape?.backgroundThreshold) {
              // Dark fabric only: remove the final subpixel of the photographed
              // white matte. Interior prints and white garment profiles are untouched.
              const alpha = new Uint8Array(w * h);
              for (let i = 0; i < alpha.length; i++) alpha[i] = rgba[i * 4 + 3];
              for (let y = Math.round(h * .18); y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
                const i = y * w + x, p = i * 4;
                if (!alpha[i]) continue;
                let edge = alpha[i];
                for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) edge = Math.min(edge, alpha[i + dy * w + dx]);
                rgba[p + 3] = edge;
              }
            }
            ctx.putImageData(pixels, 0, 0);
          }
          let minX = w, minY = h, maxX = -1, maxY = -1;
          for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            if (rgba[(y * w + x) * 4 + 3] > 12) { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); }
          }
          if (maxX < minX) throw new Error('Empty garment artwork');
          resolve({ canvas, crop: [minX, minY, maxX - minX + 1, maxY - minY + 1] });
        } catch (error) { reject(error); }
      };
      image.onerror = () => reject(new Error('Product image could not load'));
      image.src = src;
    });
    artworkCache.set(key, promise);
    promise.catch(() => artworkCache.delete(key));
    return promise;
  }

  function paint(target, art, fill, hanging = false) {
    const ctx = target.getContext('2d');
    target.width = 600; target.height = hanging ? 716 : 572;
    const [, , w, h] = art.crop;
    const scale = Math.min(600 / w, (target.height - 12) / h), outW = w * scale, outH = h * scale;
    ctx.drawImage(art.canvas, ...art.crop, (600 - outW) / 2, 0, outW, outH);
    if (fill) {
      ctx.globalCompositeOperation = 'source-in'; ctx.fillStyle = fill;
      ctx.fillRect(0, 0, target.width, target.height); ctx.globalCompositeOperation = 'source-over';
      // Garment thickness starts below the integrated wooden hanger.
      if (hanging) ctx.clearRect(0, 0, target.width, target.height * .18);
    }
  }

  function mount(root) {
    if (instances.has(root)) return;
    const configNode = root.querySelector('[data-rc-config]');
    if (!configNode) return;
    let items;
    try { items = JSON.parse(configNode.textContent); } catch { return; }
    if (!Array.isArray(items)) return;
    if (!items.length) {
      root.classList.remove('is-loading'); root.setAttribute('aria-busy', 'false');
      const emptyStatus = root.querySelector('[data-rc-status]');
      if (emptyStatus) emptyStatus.textContent = 'Add a design to start your rack.';
      const more = root.querySelector('[data-rc-more]'); if (more) more.hidden = true;
      return;
    }
    const zone = root.querySelector('[data-rc-items]'), stage = root.querySelector('[data-rc-stage]');
    const viewport = root.querySelector('[data-rc-viewport]');
    const title = root.querySelector('[data-rc-title]'), label = root.querySelector('[data-rc-label]');
    const live = root.querySelector('[data-rc-live]'), status = root.querySelector('[data-rc-status]');
    const frontButton = root.querySelector('[data-rc-front]'), backButton = root.querySelector('[data-rc-back]');
    const productLink = root.querySelector('[data-rc-product]');
    const moreButton = root.querySelector('[data-rc-more]'), count = root.querySelector('[data-rc-count]');
    // Named groups are editable in Shopify. Jerseys always form the final group.
    // Unassigned legacy blocks retain the original five-per-group behavior.
    const groupMap = new Map();
    items.forEach((item, i) => {
      const group = item.group || `auto-${Math.floor(i / 5)}`;
      if (!groupMap.has(group)) groupMap.set(group, []);
      groupMap.get(group).push(i);
    });
    const groups = [...groupMap.entries()].filter(([key]) => key !== 'jerseys').map(([, indices]) => indices);
    if (groupMap.has('jerseys')) groups.push(groupMap.get('jerseys'));
    const itemPages = [];
    groups.forEach((indices, group) => indices.forEach(i => { itemPages[i] = group; }));
    const pageCount = groups.length;
    let page = 0, busy = false, disposed = false, pagePromise;
    const abort = new AbortController(), eventOptions = { signal: abort.signal };
    let active = -1, view = 'front', interacted = false;
    const timers = [];
    const buttons = [];
    const editorBlocks = new Map(Array.from(root.querySelectorAll('[data-rc-block-id]')).map(node => [node.dataset.rcBlockId, node.getAttribute('data-shopify-editor-block')]));
    const destroy = () => { disposed = true; abort.abort(); timers.forEach(clearTimeout); observer?.disconnect(); instances.delete(root); };
    let observer;
    instances.set(root, { destroy, selectBlock });
    stage.style.minWidth = '760px';
    function sizeStage() {
      const n = groups[page].length;
      stage.style.setProperty('--rc-mobile-span', active < 0 ? '100%' : `${228 + (n - 1) * 44 + (n - 1) * 10 + 40}px`);
    }
    if (moreButton) moreButton.hidden = pageCount <= 1;
    const heading = root.dataset.idleTitle || 'Find your next favorite.';

    function stopIntro() { interacted = true; timers.forEach(clearTimeout); }
    function centerOn(button) {
      if (viewport.scrollWidth <= viewport.clientWidth) return;
      timers.push(setTimeout(() => {
        if (!root.isConnected) return;
        const left = button.getBoundingClientRect().left - viewport.getBoundingClientRect().left + viewport.scrollLeft;
        viewport.scrollTo({ left: left + button.offsetWidth / 2 - viewport.clientWidth / 2, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }, reducedMotion.matches ? 0 : 750));
    }
    function updateDetail(announce) {
      const item = items[active];
      title.textContent = item ? item.title : heading;
      label.textContent = item ? (view === 'back' ? 'Back print' : 'Front print') : (root.dataset.idleLabel || 'Core Collection');
      frontButton.disabled = !item; backButton.disabled = !item || !item.back;
      frontButton.setAttribute('aria-pressed', String(!!item && view === 'front'));
      backButton.setAttribute('aria-pressed', String(!!item && view === 'back'));
      if (productLink) {
        productLink.hidden = !item?.url;
        if (item?.url) {
          try {
            const url = new URL(item.url, location.href);
            if (url.protocol === 'https:' || url.protocol === 'http:') productLink.href = item.url;
            else productLink.hidden = true;
          } catch { productLink.hidden = true; }
        }
      }
      if (announce && item) live.textContent = `${item.title}, ${view === 'back' ? 'back' : 'front'} view`;
    }
    function activate(index, { announce = false, scroll = false, back = false } = {}) {
      if (index >= 0 && (busy || itemPages[index] !== page)) return;
      active = index; view = back && items[index]?.back ? 'back' : 'front';
      buttons.forEach((button, i) => {
        button.classList.toggle('is-active', i === index);
        button.classList.toggle('is-back', i === index && view === 'back');
        button.setAttribute('aria-pressed', String(i === index));
      });
      sizeStage();
      updateDetail(announce);
      if (scroll && buttons[index]) centerOn(buttons[index]);
    }
    function setView(next) {
      if (active < 0 || (next === 'back' && !items[active].back)) return;
      view = next; buttons[active].classList.toggle('is-back', next === 'back'); updateDetail(true);
    }
    async function selectBlock(id) {
      const i = items.findIndex(item => item.blockId === id);
      if (i < 0) return;
      stopIntro();
      await pagePromise;
      if (disposed) return;
      const nextPage = itemPages[i];
      if (nextPage !== page) { pagePromise = showPage(nextPage); await pagePromise; }
      if (!disposed) activate(i, { scroll: true });
    }
    function nextIndex(index, delta) {
      const indices = groups[page], current = indices.indexOf(index);
      return current < 0 ? indices[delta > 0 ? 0 : indices.length - 1] : indices[(current + delta + indices.length) % indices.length];
    }

    let swipeStart = null, suppressClickUntil = 0;
    viewport.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch' || !compact.matches || busy) return;
      swipeStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }, eventOptions);
    viewport.addEventListener('pointercancel', () => { swipeStart = null; }, eventOptions);
    viewport.addEventListener('pointerup', event => {
      const start = swipeStart; swipeStart = null;
      if (!start || event.pointerId !== start.id || busy) return;
      const dx = event.clientX - start.x, dy = event.clientY - start.y;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
      suppressClickUntil = Date.now() + 450; stopIntro();
      activate(nextIndex(active, dx < 0 ? 1 : -1), { announce: true, scroll: true });
    }, eventOptions);
    compact.addEventListener?.('change', () => {
      if (disposed || busy) return;
      if (compact.matches && active < 0) activate(groups[page][0]);
      sizeStage(); if (compact.matches && active >= 0) centerOn(buttons[active]);
    }, eventOptions);

    const loadJobs = items.map((item, i) => {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'rc-rack__slot'; button.dataset.rcIndex = String(i);
      if (editorBlocks.get(item.blockId)) button.setAttribute('data-shopify-editor-block', editorBlocks.get(item.blockId));
      button.setAttribute('aria-label', `Explore ${item.title}`); button.setAttribute('aria-pressed', 'false');
      button.innerHTML = '<span class="rc-rack__rig" aria-hidden="true"><svg class="rc-rack__hanger" viewBox="0 0 300 140"><path d="M151 4 C174 -1 182 20 165 29 L150 42 L150 58" fill="none" stroke="#939b99" stroke-width="3" stroke-linecap="round"/><path d="M150 58 L61 101 Q53 106 66 108 L234 108 Q247 106 239 101 Z" fill="#262a27" stroke="#6d746b" stroke-width="2.6" stroke-linejoin="round"/><path d="M70 103 L230 103" stroke="#369c4e" stroke-opacity=".22" stroke-width="1"/></svg><canvas class="rc-rack__canvas rc-rack__layer" data-layer="-3"></canvas><canvas class="rc-rack__canvas rc-rack__layer" data-layer="0"></canvas><canvas class="rc-rack__canvas rc-rack__layer" data-layer="3"></canvas><canvas class="rc-rack__canvas rc-rack__front"></canvas><canvas class="rc-rack__canvas rc-rack__back"></canvas></span>';
      button.classList.toggle('is-hanging', !!item.hanging);
      const rig = button.querySelector('.rc-rack__rig');
      const motion = document.createElement('span'); motion.className = 'rc-rack__motion';
      while (rig.firstChild) motion.appendChild(rig.firstChild);
      rig.appendChild(motion);
      zone.appendChild(button); buttons.push(button);
      button.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        stopIntro();
        if (active !== i) activate(i);
      }, eventOptions);
      button.addEventListener('click', event => {
        if (Date.now() < suppressClickUntil) return;
        stopIntro(); activate(i, { announce: true, scroll: event.pointerType !== 'mouse' });
      }, eventOptions);
      button.addEventListener('focus', () => { if (!interacted) stopIntro(); if (active !== i) activate(i, { announce: true, scroll: true }); }, eventOptions);
      button.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); stopIntro();
          if (busy) return;
          const next = nextIndex(i, event.key === 'ArrowRight' ? 1 : -1);
          buttons[next].focus(); activate(next, { announce: true, scroll: true });
        } else if (event.key.toLowerCase() === 'b') { event.preventDefault(); setView(view === 'front' ? 'back' : 'front'); }
      }, eventOptions);
      const knockout = item.knockout !== false;
      button.hidden = itemPages[i] !== 0;
      let job;
      return () => job || (job = Promise.all([readArtwork(item.front, knockout, item.frontRegion, item.frontClip), item.back ? readArtwork(item.back, knockout, item.backRegion, item.backClip) : readArtwork(item.front, knockout, item.frontRegion, item.frontClip)]).then(([front, back]) => {
        if (disposed || !root.isConnected) return;
        paint(button.querySelector('.rc-rack__front'), front, null, item.hanging);
        paint(button.querySelector('.rc-rack__back'), back, null, item.hanging);
        button.querySelectorAll('[data-layer]').forEach(layer => { paint(layer, front, item.color || '#272727', item.hanging); layer.style.transform = `translateZ(${layer.dataset.layer}px)`; });
      }).catch(() => { button.disabled = true; button.setAttribute('aria-label', `${item.title}: image unavailable`); throw new Error(`Image unavailable: ${item.title}`); }));
    });
    // Keep the last hovered tee selected while the cursor travels to its controls.
    // Another garment, a group change or Escape changes the selection explicitly.
    root.addEventListener('keydown', event => { if (event.key === 'Escape') activate(-1); }, eventOptions);
    frontButton.addEventListener('click', () => { stopIntro(); setView('front'); }, eventOptions);
    backButton.addEventListener('click', () => { stopIntro(); setView('back'); }, eventOptions);
    root.querySelectorAll('[data-rc-step]').forEach(button => button.addEventListener('click', () => {
      stopIntro(); const delta = Number(button.dataset.rcStep);
      activate(nextIndex(active, delta), { announce: true, scroll: true });
    }, eventOptions));
    updateDetail(false);
    async function showPage(next, animate = true) {
      busy = true;
      activate(-1);
      if (moreButton) moreButton.disabled = true;
      root.setAttribute('aria-busy', 'true');
      root.classList.add('is-changing');
      const wait = animate && !reducedMotion.matches ? new Promise(resolve => setTimeout(resolve, 180)) : Promise.resolve();
      const indices = groups[next], shown = new Set(indices);
      const loading = Promise.allSettled(indices.map(i => loadJobs[i]()));
      await wait;
      if (disposed) return;
      page = next;
      buttons.forEach((button, i) => { button.hidden = !shown.has(i); });
      if (count) count.textContent = `${page + 1} / ${pageCount}`;
      viewport.scrollTo({ left: 0, behavior: 'auto' });
      const results = await loading;
      if (disposed || !root.isConnected) return;
      const failed = results.filter(result => result.status === 'rejected').length;
      status.textContent = failed ? 'Some designs could not load. Please refresh to try again.' : '';
      status.hidden = !failed;
      root.classList.remove('is-loading', 'is-changing');
      root.setAttribute('aria-busy', 'false'); busy = false;
      sizeStage();
      if (compact.matches && !failed) activate(indices[0], { scroll: true });
      if (moreButton) moreButton.disabled = false;
      if (animate) live.textContent = `Group ${page + 1} of ${pageCount}: ${indices.map(i => items[i].title).join(', ')}`;
      return failed;
    }
    moreButton?.addEventListener('click', () => {
      if (busy) return;
      stopIntro(); pagePromise = showPage((page + 1) % pageCount);
    }, eventOptions);
    pagePromise = showPage(0, false);
    pagePromise.then(failed => {
      if (disposed || !root.isConnected) return;
      if (failed || reducedMotion.matches || root.dataset.intro === 'false') return;
      const intro = () => {
        if (interacted) return;
        const i = Math.min(1, items.length - 1);
        timers.push(setTimeout(() => { if (!interacted) { activate(i); centerOn(buttons[i]); } }, 250));
        timers.push(setTimeout(() => { if (!interacted && items[i].back) setView('back'); }, 1600));
        timers.push(setTimeout(() => { if (!interacted) activate(-1); }, 3150));
      };
      observer = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) { observer.disconnect(); intro(); } }, { threshold: .3 });
      observer.observe(root);
    });
  }
  function scan(scope = document) {
    if (scope.matches?.('[data-rc-rack]')) mount(scope);
    scope.querySelectorAll?.('[data-rc-rack]').forEach(mount);
  }
  function destroy(scope = document) {
    if (scope.matches?.('[data-rc-rack]')) instances.get(scope)?.destroy();
    scope.querySelectorAll?.('[data-rc-rack]').forEach(root => instances.get(root)?.destroy());
  }
  window.RaversRack = { scan, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true }); else scan();
  {
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('shopify:section:unload', event => {
      event.target.querySelectorAll('[data-rc-rack]').forEach(root => instances.get(root)?.destroy());
    });
    document.addEventListener('shopify:block:select', event => {
      document.querySelectorAll('[data-rc-rack]').forEach(root => instances.get(root)?.selectBlock(event.detail.blockId));
    });
  }
})();
